import type { MomentNotificationsResponse } from '../../AlertsAndNotification/store/types';

/** Callback after PATCH — pass the raw API body when available to skip follow-up GETs. */
export type SlideoutPatchRefreshCallback = (
  patchResponse?: unknown
) => void | Promise<void>;

/** Options for slideout refresh after PATCH (moment / notifications / features list). */
export type SlideoutSeamlessRefreshOptions = {
  /** When false, skips GET /features/issueId/… after applying moment data. Default: true. */
  refreshFeatures?: boolean;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Extract full moment payload from PATCH envelope or legacy flat body.
 * Supports `{ data: { moment } }` and `{ moment }`.
 */
export const getMomentFromPatchEnvelope = (
  patchResponse?: unknown
): Record<string, unknown> | null => {
  if (!isRecord(patchResponse)) {
    return null;
  }

  const moment =
    patchResponse.data && isRecord(patchResponse.data)
      ? patchResponse.data.moment
      : patchResponse.moment;

  if (!isRecord(moment)) {
    return null;
  }

  const issueId = moment.issueId;
  if (typeof issueId !== 'string' || !issueId.trim()) {
    return null;
  }

  return moment;
};

/**
 * Extract moment-scoped notifications from PATCH envelope.
 * Supports `{ data: { notifications } }` and `{ notifications }`.
 */
export const getNotificationsFromPatchEnvelope = (
  patchResponse?: unknown
): MomentNotificationsResponse | null => {
  if (!isRecord(patchResponse)) {
    return null;
  }

  const rawNotifications =
    patchResponse.data && isRecord(patchResponse.data)
      ? patchResponse.data.notifications
      : patchResponse.notifications;

  if (!isRecord(rawNotifications)) {
    return null;
  }

  const hasNotificationsPayload =
    'notifications' in rawNotifications ||
    'unreadCount' in rawNotifications ||
    'alertCount' in rawNotifications ||
    'criticalCount' in rawNotifications ||
    'syncStatus' in rawNotifications ||
    'momentVersion' in rawNotifications ||
    'notificationVersion' in rawNotifications;

  if (!hasNotificationsPayload) {
    return null;
  }

  return {
    notifications: Array.isArray(rawNotifications.notifications)
      ? rawNotifications.notifications
      : [],
    unreadCount: Number(rawNotifications.unreadCount ?? 0),
    alertCount: Number(rawNotifications.alertCount ?? 0),
    criticalCount: Number(rawNotifications.criticalCount ?? 0),
    syncStatus: rawNotifications.syncStatus === 'STALE' ? 'STALE' : 'FRESH',
    momentVersion: Number(rawNotifications.momentVersion ?? 0),
    notificationVersion: Number(rawNotifications.notificationVersion ?? 0)
  };
};
